// AppShell.jsx
// The one responsive frame for the authenticated app. Swaps navigation chrome
// by device tier while rendering the same routed content:
//   desktop / tablet → existing Sidebar + Navbar (today's layout, untouched)
//   phone            → MobileHeader + floating BottomTabBar + More sheet
//
// This dedupes the chrome that MainLayout/WithLayout used to inline. It renders
// chrome only — data providers (WebSocket, etc.) stay in the caller.
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sidebar, Navbar, ThemeSettings } from "../";
import { useStateContext } from "../../contexts/ContextProvider";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import MobileHeader from "./MobileHeader";
import BottomTabBar from "./BottomTabBar";
import MoreSheet from "./MoreSheet";
import NavRail from "./NavRail";

// Full-width content column — data/graph screens (SmartTrade, dashboards) should
// use the whole canvas on large monitors, not sit centered in a fixed column.
// Extra real estate becomes MORE content (denser grids, bigger charts), which is
// each page's own responsibility via its responsive grid; the shell just gets
// out of the way. min-w-0 keeps flex children from overflowing.
// `bounded` (opt-in, BOUNDED_ROUTES): Content becomes a flex column that fills the
// definite-height row below the navbar, so the page fills it with flex instead of
// per-page viewport arithmetic.
const Content = ({ bounded = false, children }) => (
  <div className={bounded ? "w-full min-w-0 flex flex-col flex-1 min-h-0" : "w-full min-w-0"}>
    {children}
  </div>
);

// Route → phone header title/subtitle. Prefix match, longest wins.
const TITLES = [
  { p: "/Overview", t: "Dashboard", s: "Markets open · IST" },
  { p: "/Portfolio", t: "Portfolio", s: "Your holdings" },
  { p: "/WatchList", t: "Watch List", s: "My List" },
  { p: "/SmartTrade", t: "Trade", s: "Order entry" },
  { p: "/TradeSetup", t: "Trade", s: "Manual order" },
  { p: "/Orders", t: "Orders", s: "Orders & fills" },
  { p: "/AlgoDashboard", t: "Algos", s: "Live strategies" },
  { p: "/Strategies/Modern/Backtesting", t: "Backtesting", s: "Strategy runs" },
  { p: "/Strategies", t: "Strategies", s: "Build & monitor" },
  //! "Broker Accounts" is the ONE name for this screen — it used to answer to
  //! three ("Brokers" here, "Trade Accounts" in its PageHeader, "Broker
  //! Platforms" in the nav). It lists connected ACCOUNTS, which is also what its
  //! own sub-flows say ("Add Account"). "Broker Platforms" stays the SECTION.
  { p: "/AddTradeAccount", t: "Add Account", s: "Connect a broker" },
  { p: "/TradeAccounts", t: "Broker Accounts", s: "Your connected accounts" },
  { p: "/InstrumentMaster", t: "Instruments", s: "NSE · BSE · F&O" },
  //! The Admin section's pages each need their OWN entry. Without them the
  //! phone header fell back to a bare "DeeTrade" (/user-management,
  //! /BrokerManagement, /MarketDataManagement), and /Admin/BatchJobs
  //! prefix-matched /Admin and mislabelled itself "Admin · Live connections".
  //! Longest prefix wins, so /Admin/BatchJobs must precede /Admin here.
  { p: "/Admin/BatchJobs", t: "Batch Jobs", s: "Scheduled runs" },
  { p: "/Admin/Models", t: "Models", s: "Model registry" },
  { p: "/Admin/TradeEconomics", t: "Trade Economics", s: "Charges & commissions" },
  { p: "/Admin/LiveConnections", t: "Live Connections", s: "Sessions & devices" },
  { p: "/Admin", t: "Live Connections", s: "Sessions & devices" },
  { p: "/user-management", t: "User Management", s: "Roles & access" },
  { p: "/BrokerManagement", t: "Broker Management", s: "Platforms & credentials" },
  { p: "/MarketDataManagement", t: "Market Data", s: "Feeds & subscriptions" },
  { p: "/profile", t: "Profile", s: "Your account" },
];

// Routes that render their OWN context bar on phone (SmartTrade's slim instrument
// bar carries the symbol + live LTP + back crumb). Stacking the shell's generic
// header on top of that just duplicates the title and eats vertical space on a
// phone, so the shell yields its header to them.
const OWN_HEADER_ROUTES = ["/SmartTrade"];

// Routes whose page fills the viewport below the navbar with flex (no document
// scroll). Opt-in: every other route keeps the min-h-screen row + plain Content.
const BOUNDED_ROUTES = [/^\/AlgoDashboard\/[^/]+\/?$/i];
const isBoundedRoute = (pathname) => BOUNDED_ROUTES.some((re) => re.test(pathname));

function headerFor(pathname) {
  let best = { t: "DeeTrade", s: "" };
  let bestLen = -1;
  for (const e of TITLES) {
    if (pathname.toLowerCase().startsWith(e.p.toLowerCase()) && e.p.length > bestLen) {
      best = e;
      bestLen = e.p.length;
    }
  }
  return best;
}

export default function AppShell({ children }) {
  const { isPhone, isTablet } = useBreakpoint();
  const { activeMenu, themeSettings } = useStateContext();
  const { pathname } = useLocation();
  const bounded = isBoundedRoute(pathname);
  const [moreOpen, setMoreOpen] = useState(false);

  // Close the More sheet whenever the route changes (e.g. after a tap-through).
  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  // ── Phone tier ─────────────────────────────────────────────────────────
  if (isPhone) {
    const { t, s } = headerFor(pathname);
    const ownsHeader = OWN_HEADER_ROUTES.some((p) =>
      pathname.toLowerCase().startsWith(p.toLowerCase()),
    );
    return (
      <div
        style={{
          // 100dvh (dynamic viewport), not 100vh: on mobile 100vh is the LARGEST
          // viewport (taller than the visible area), leaving the shell a hair
          // taller than the screen — a persistent sub-pixel misfit that can show
          // a hairline at the top on scroll in light mode. 100dvh = the actual
          // visible height, so the shell fits exactly.
          minHeight: "100dvh",
          background: "var(--bg-1)",
          // Kill the browser's native pull-to-refresh — the #1 "this is a
          // website" tell. Our own PTR comes later.
          overscrollBehaviorY: "contain",
        }}
      >
        {themeSettings && <ThemeSettings />}
        {!ownsHeader && <MobileHeader title={t} subtitle={s} onAvatar={() => setMoreOpen(true)} />}
        <main
          style={{
            // Clear the floating tab bar (60h + 12 bottom + margin + safe area).
            padding: "0 0 calc(96px + env(safe-area-inset-bottom, 0px))",
            minHeight: 0,
          }}
        >
          {children}
        </main>
        <BottomTabBar moreOpen={moreOpen} onMore={() => setMoreOpen((v) => !v)} />
        <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
      </div>
    );
  }

  // ── Tablet tier — icon rail + navbar + capped content ──────────────────
  if (isTablet) {
    return (
      <div className={`flex bg-bg-3 dark:bg-main-dark-bg ${bounded ? "h-[100dvh]" : "min-h-screen"}`}>
        <NavRail />
        {themeSettings && <ThemeSettings />}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="sticky top-0 z-40 navbar w-full bg-bg-3/80 dark:bg-main-dark-bg/80 backdrop-blur-md border-b border-[var(--divider)]">
            <Navbar />
          </div>
          <Content bounded={bounded}>{children}</Content>
        </div>
      </div>
    );
  }

  // ── Desktop + Wide tiers — full sidebar + navbar + capped content ───────
  return (
    <div className={`flex bg-bg-3 dark:bg-main-dark-bg ${bounded ? "h-[100dvh]" : "min-h-screen"}`}>
      <motion.div
        initial={false}
        animate={{ width: activeMenu ? "13rem" : "0rem" }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="bg-bg-2 h-screen sticky top-0 overflow-hidden z-50 shrink-0"
      >
        <div className="w-52 h-full">
          <Sidebar />
        </div>
      </motion.div>

      {themeSettings && <ThemeSettings />}

      <div className="flex-1 flex flex-col min-w-0">
        <div className="sticky top-0 z-40 navbar w-full bg-bg-3/80 dark:bg-main-dark-bg/80 backdrop-blur-md border-b border-[var(--divider)]">
          <Navbar />
        </div>
        <Content bounded={bounded}>{children}</Content>
      </div>
    </div>
  );
}
